import React from 'react';
import {
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { HourlyViews } from '../models/Statistics';

interface Props {
    data: HourlyViews | null;
    period: 'last7days' | 'last30days';
}

const PEAK_COLOR = '#1A3B40';
const BASE_COLOR = '#4399A6';

const HourlyTrafficChart: React.FC<Props> = ({ data, period }) => {
    const { t } = useTranslation();

    // Fill all 24 hours even if backend omits zero-count hours
    const raw = data?.[period] ?? [];
    const byHour = Object.fromEntries(raw.map(h => [h.hour, h.count]));
    const chartData = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        label: `${String(hour).padStart(2, '0')}:00`,
        count: byHour[hour] ?? 0,
    }));

    const maxCount = Math.max(...chartData.map(d => d.count), 1);

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload?.length) return null;
        const { label, count } = payload[0].payload;
        return (
            <div className="bg-white border border-neutral rounded p-2 text-sm shadow">
                <span className="font-semibold text-primary-dark">{label}</span>
                <span className="ml-2 text-utility-mutedText">{count.toLocaleString()} {t('statistics.visitors')}</span>
            </div>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0eeef" vertical={false} />
                <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    interval={1}
                    tickFormatter={(v) => v.replace(':00', 'h')}
                />
                <YAxis tick={{ fontSize: 12 }} width={36} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f0f9fa' }} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {chartData.map((entry) => (
                        <Cell
                            key={`cell-${entry.hour}`}
                            fill={entry.count === maxCount ? PEAK_COLOR : BASE_COLOR}
                            fillOpacity={0.7 + (entry.count / maxCount) * 0.3}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default HourlyTrafficChart;
